import type { StaticImageData } from "next/image";

import bagPassHepaPoster from "@assets/images/shop-overview/bag-pass-hepa-poster.jpg";
import cleaningFilmPoster from "@assets/images/shop-overview/cleaning-film-poster.jpg";
import maticCareFilmPoster from "@assets/images/shop-overview/matic-care-film-poster.jpg";
import cuesVoicePoster from "@assets/images/hey-matic/theater/voice-desktop.webp";
import sereneYogaPoster from "@assets/images/shop-overview/serene-quiet-yoga-poster.jpg";
import { config } from "@/config";
import { INNOVATION_TABS } from "@/views/innovation/innovationContent";
import { LIFE_PROOF_VISUALS } from "@/views/life-with-matic/lifeAssets";
import {
  type FilmMoment,
  type MaticModelId,
  UNCAPTIONED_FILM,
} from "@/views/shop/shopContent";

/**
 * One clip in a feature tab's playlist. Videos carry the poster and alt the
 * page they come from already pairs with them; a still holds for a while.
 */
export type FeatureClip =
  | {
      id: string;
      kind: "video";
      video: string;
      poster: StaticImageData;
      alt: string;
      /**
       * Lines timed to the clip's shots, captioning it in place of its
       * bullet as it plays (a film); null for a clip its bullet names.
       */
      moments: readonly FilmMoment[] | null;
    }
  | {
      id: string;
      kind: "image";
      /** Null until the asset exists; the clip then drops out of the cycle. */
      image: StaticImageData | null;
      alt: string;
      holdMs: number;
    };

/** A note-card line, tied to the clip that plays while it's lit. */
export type FeatureBullet = { text: string; clipId: string };

/** The tab ids; the labels and content are below. */
export type FeatureTabId =
  "overview" | "cleaning" | "vision" | "voice" | "app" | "quiet" | "design";

export type BenefitTab = {
  id: Exclude<FeatureTabId, "overview" | "design">;
  label: string;
  title: string;
  bullets: readonly FeatureBullet[];
  /** The playlist, in play order; bullets sharing a clip light together. */
  clips: readonly FeatureClip[];
};

/**
 * A Life with Matic proof clip with the poster and alt that page pairs it
 * with (LIFE_PROOF_VISUALS), found by its video so the pairing can't drift.
 */
const lifeClip = (id: string, video: string): FeatureClip => {
  const visual = Object.values(LIFE_PROOF_VISUALS)
    .flat()
    .find((item) => item.video === video);
  if (visual === undefined)
    throw new Error(`No Life with Matic visual plays ${video}`);
  return {
    id,
    kind: "video",
    video,
    poster: visual.image,
    alt: visual.alt,
    moments: null,
  };
};

/**
 * An Innovation topic's clip with its poster and alt (INNOVATION_TABS), found by its video for the same reason.
 */
const innovationClip = (
  id: string,
  video: string,
  moments: readonly FilmMoment[] | null = null,
): FeatureClip => {
  const media = INNOVATION_TABS.flatMap(({ topics }) => topics)
    .map(({ media: topicMedia }) => topicMedia)
    .find((item) => item?.video === video);
  if (media === undefined)
    throw new Error(`No Innovation topic plays ${video}`);
  return {
    id,
    kind: "video",
    video,
    poster: media.poster,
    alt: media.alt,
    moments,
  };
};

/**
 * The Cleaning tab's film (Shop page / VIDEO / FINAL / MATIC CLEANING), cut
 * for the web: silent, 1080p, 38.7s, 3.7 MB; poster at 1s, on the rug. Its
 * caption runs with it, a line per shot from the cuts, each the site's own:
 * the Vision and Cleaning tabs, Innovation's design notes, the FAQ's tank.
 */
const cleaningFilm: FeatureClip = {
  id: "cleaning-film",
  kind: "video",
  video: `${config.media.baseUrl}/videos/cleaning-film.mp4`,
  poster: cleaningFilmPoster,
  alt: "Matic cleaning a home: crumbs on a rug, pet hair at the baseboard, a spill on hardwood, and its bag emptied into the trash",
  moments: [
    // Crumbs on a shag rug.
    {
      from: 0,
      text: "Big wheels make easy work of rugs and thresholds up to 0.9″",
    },
    // Pet hair along the baseboard, then the toe-kick.
    {
      from: 3.7,
      text: "Gets into corners and under cabinets, where crumbs love to hide",
    },
    // Crumbs by the kitchen cabinets and under the sofa.
    { from: 8.4, text: "Vacuums and mops in one go" },
    // The lid lifted on the tank and the bag.
    {
      from: 18.5,
      text: "Change the bag and refill the tank, all under one flap",
    },
    // Water from the sink, the tank set back.
    {
      from: 21.1,
      text: "Mops about 700 sq ft on a 600 mL tank",
    },
    // A spill on hardwood, mopped up.
    {
      from: 26,
      text: "Cleans its mop as it goes, lifting dirt instead of spreading it",
    },
    // The bag pulled and dropped in the trash.
    {
      from: 32.75,
      text: "Dry debris and dirty water, all sealed in one HEPA-12 bag",
    },
  ],
};

const cleaning: BenefitTab = {
  id: "cleaning",
  label: "Cleaning",
  title: "Cleaning",
  // The film captions itself as it plays.
  bullets: [],
  clips: [cleaningFilm],
};

const vision: BenefitTab = {
  id: "vision",
  label: "Vision",
  title: "Sees your home",
  bullets: [
    {
      text: "Five cameras recognize what’s on the floor, from stray socks and shoes to the crumbs around them",
      clipId: "visual-perception",
    },
    {
      text: "Maps every floor in 3D, adapting as your home changes",
      clipId: "smart-mapping",
    },
    {
      text: "Recognizes rugs and keeps the mop off them",
      clipId: "all-terrain",
    },
    {
      text: "On-device processing keeps camera feeds and maps private by default",
      clipId: "privacy",
    },
  ],
  // The first two are the Innovation page's: Life with Matic has no vision
  // or mapping clip.
  clips: [
    innovationClip(
      "visual-perception",
      `${config.media.baseUrl}/videos/innovation-visual-perception.mp4`,
    ),
    innovationClip(
      "smart-mapping",
      `${config.media.baseUrl}/videos/innovation-smart-mapping.mp4`,
    ),
    lifeClip(
      "all-terrain",
      `${config.media.baseUrl}/videos/life-all-terrain.mp4`,
    ),
    lifeClip("privacy", `${config.media.baseUrl}/videos/life-privacy.mp4`),
  ],
};

/**
 * Voice plays the second full-bleed film on /hey-matic (Matic Cues): "Go
 * hands-free.", its desktop cut (1080p, 8.9s), under that section's
 * subtitle.
 */
const voice: BenefitTab = {
  id: "voice",
  label: "Voice",
  title: "Just tell Matic",
  bullets: [
    { text: "Hands-free cleaning, just by asking", clipId: "cues-voice" },
  ],
  clips: [
    {
      id: "cues-voice",
      kind: "video",
      video: `${config.media.baseUrl}/videos/hey-matic-theater-voice-desktop-v2.mp4`,
      poster: cuesVoicePoster,
      alt: "A girl asks Matic to clean a spill, and it wipes the tile floor",
      moments: null,
    },
  ],
};

/**
 * App plays what Voice used to: Life with Matic's app clips. A spot tapped on
 * the map, Matic heading to it, then a schedule set.
 */
const app: BenefitTab = {
  id: "app",
  label: "App",
  title: "Matic app",
  bullets: [
    { text: "Send Matic to any spot with a tap", clipId: "tap-the-map" },
    { text: "Send Matic to any spot with a tap", clipId: "spot-cleaning" },
    {
      text: "Tailor every clean to your routine",
      clipId: "easy-scheduling",
    },
  ],
  // Life's "Hey Matic, clean this" card plays life-clean-on-command (a tap on
  // the app's map) and its "Clean on command" card life-spot-cleaning
  // (LIFE_PROOF_VISUALS).
  clips: [
    lifeClip(
      "tap-the-map",
      `${config.media.baseUrl}/videos/life-clean-on-command.mp4`,
    ),
    lifeClip(
      "spot-cleaning",
      `${config.media.baseUrl}/videos/life-spot-cleaning.mp4`,
    ),
    lifeClip(
      "easy-scheduling",
      `${config.media.baseUrl}/videos/life-easy-scheduling.mp4`,
    ),
  ],
};

const quietMatic: BenefitTab = {
  id: "quiet",
  label: "Quiet",
  title: "Quiet by design",
  bullets: [
    {
      text: "Quiet enough for work, rest, and everything in between",
      clipId: "quiet",
    },
  ],
  clips: [
    lifeClip("quiet", `${config.media.baseUrl}/videos/life-quiet.mp4`),
    // The product shot carries its own decibel badge, so no caption runs
    // under it.
    innovationClip(
      "quiet-engineering",
      `${config.media.baseUrl}/videos/innovation-quiet.mp4`,
      UNCAPTIONED_FILM,
    ),
  ],
};

/**
 * Serene's quiet film (16X9_MATIC_YOGA EVRGRN), cut to end at 27.2s before
 * its closing logo, silent, 1080p, 6.7 MB; poster at 3s. A woman's yoga practice while Matic cleans around her.
 */
const sereneYogaFilm: FeatureClip = {
  id: "serene-yoga",
  kind: "video",
  video: `${config.media.baseUrl}/videos/serene-quiet-yoga.mp4`,
  poster: sereneYogaPoster,
  alt: "A woman doing yoga in a sunlit room while Matic cleans nearby",
  moments: [
    { from: 0, text: null },
    // Matic passing her feet as she sets up.
    { from: 2.75, text: "A softer sound, by design" },
    // Along the rug's edge, beside her.
    {
      from: 6.9,
      text: "High-frequency tones softened for a smoother, more even sound",
    },
    // She sits, eyes closed, Matic behind her.
    { from: 13.2, text: "52 dB. Our quietest Matic yet" },
    // The wide shots of her practice, to the end.
    { from: 20.6, text: null },
  ],
};

const quietSerene: BenefitTab = {
  id: "quiet",
  label: "Serene Quiet",
  title: "Matic Serene",
  // The yoga film captions itself as it plays.
  bullets: [],
  // The yoga film alone, looping: Serene's quiet, shown rather than listed.
  clips: [sereneYogaFilm],
};

/**
 * The visual zone's feature tabs for the title and model blocks, after
 * Tesla's Model Y design page: Overview plays the film, Matic Design turns
 * the 3D model, and every other tab plays a playlist of clips, each captioned
 * by its bullet. The models share every tab but the quiet one.
 */
export const FEATURE_TABS = {
  label: "Matic features",
  overview: { id: "overview" as const, label: "Overview" },
  /** The real-time 3D model, turned any way by hand. */
  design: {
    id: "design" as const,
    label: "3D View",
    /** Over the tabs, under the turntable; never on mobile. */
    hint: "Drag to rotate",
  },
  shared: [cleaning, vision, voice, app],
  quiet: { matic: quietMatic, "matic-serene": quietSerene } satisfies Record<
    MaticModelId,
    BenefitTab
  >,
  /** How long the quiet tab marks a model switch, and what's announced. */
  changedMs: 2000,
  quietUpdated: (modelTitle: string) => `Quiet tab updated for ${modelTitle}`,
} as const;

/**
 * A model's tabs in order: Overview, its quiet one (the difference between
 * the models, so it leads), the shared ones, then the 3D model.
 */
export const featureTabsFor = (modelId: MaticModelId) =>
  [
    FEATURE_TABS.overview,
    FEATURE_TABS.quiet[modelId],
    ...FEATURE_TABS.shared,
    FEATURE_TABS.design,
  ] as const;

/**
 * A hand at the HEPA bag, cut for the web from the old shop page's
 * full-length product video (no longer kept in the repo): 65.27-68.86s,
 * the bag from above, then the bin lifted out. 3.6s, 0.5 MB; poster at 1s.
 */
const bagPassHepa: FeatureClip = {
  id: "bag-pass-hepa",
  kind: "video",
  video: `${config.media.baseUrl}/videos/bag-pass-hepa.mp4`,
  poster: bagPassHepaPoster,
  alt: "A hand fitting Matic's HEPA bag, then lifting the bin out",
  moments: null,
};

/**
 * Matic Care's film, cut from the old shop page's full-length product
 * video (no longer kept in the repo): Matic unboxed and set down (0-8.25s,
 * up to the cut to another robot on a rug), then Matic turning on black
 * (83.42-88.55s). 13.4s, 2.1 MB; poster at 1s.
 */
const maticCareFilm: FeatureClip = {
  id: "matic-care-film",
  kind: "video",
  video: `${config.media.baseUrl}/videos/matic-care-film.mp4`,
  poster: maticCareFilmPoster,
  alt: "Matic lifted from its box and set down, then turning on a dark ground",
  moments: null,
};

/**
 * What the add-ons without imagery of their own play, in the visual zone and
 * the details dialog: a sequence, looping.
 */
export const ADD_ON_CLIPS = {
  "annual-bag-pass": [
    bagPassHepa,
    innovationClip(
      "hepa-bag",
      `${config.media.baseUrl}/videos/innovation-hepa.mp4`,
    ),
  ],
  "matic-care": [maticCareFilm],
} satisfies Record<string, readonly FeatureClip[]>;

export type AddOnClipsId = keyof typeof ADD_ON_CLIPS;
